import { saveAs } from 'file-saver';

export const API_HOST = process.env.REACT_APP_API_HOST
export const APP_ENV = process.env.REACT_APP_ENV

export const BIN_JWT_TOKEN = 'bin_jwt_token'
export const BIN_REFRESH_TOKEN = 'bin_refresh_token'

export function deleteToken() {
  return localStorage.removeItem(BIN_JWT_TOKEN);
}

export function getToken() {
  return localStorage.getItem(BIN_JWT_TOKEN);
}

export function getRefreshToken() {
  return localStorage.getItem(BIN_REFRESH_TOKEN);
}

export function deleteRefreshToken() {
  return localStorage.removeItem(BIN_REFRESH_TOKEN);
}

export function deleteAllTokens() {
  localStorage.removeItem(BIN_JWT_TOKEN);
  localStorage.removeItem(BIN_REFRESH_TOKEN);
}

// Singleton promise to prevent parallel refresh calls
let refreshPromise = null;

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${API_HOST}auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.status === 200) {
        const data = await response.json();
        localStorage.setItem(BIN_JWT_TOKEN, data.token);
        localStorage.setItem(BIN_REFRESH_TOKEN, data.refresh_token);
        return true;
      }

      // Refresh failed — tokens are invalid
      deleteAllTokens();
      return false;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function redirectToLogin() {
  sessionStorage.setItem('return_to', window.location.href);
  window.location.href = '/login';
}

export async function fetcher(url, method='GET', data = null, secure = true, formData = false) {
  if (method === 'GET') {
    return getFetcher(url, secure);
  } else {
    return postFetcher(url, method, data, secure, formData);
  }
}

async function getFetcher(url, secure) {
  try {
    const headers = {
      'Content-Type': 'application/json'
    }

    if (secure) {
      headers['Authorization'] = 'Bearer ' + getToken();
    }

		const response = await fetch(url, {
			method: 'GET',
			headers: headers,
		})

		if (response.status === 200) {
			return response.json();
		} if (response.status === 401 || response.status === 403) {
      if (secure) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          const retryHeaders = { ...headers, 'Authorization': 'Bearer ' + getToken() };
          const retryResponse = await fetch(url, { method: 'GET', headers: retryHeaders });
          if (retryResponse.status === 200) return retryResponse.json();
        }
      }
      redirectToLogin();
		} if (response.status === 500) {
		  const error = await response.json();
		  throw new Error( error.exception);
	  	}
	} catch (error) {
	  throw error;
	}
}

async function postFetcher(url, method, data, secure, formData = false) {
  try {
    let body = null;
    let headers = {};

    if (formData) {
      headers = {};
      body = data;
    } else {
      headers = { 'Content-Type': 'application/json' }
      body = JSON.stringify(data);
    }

    if (secure) {
      headers['Authorization'] = 'Bearer ' + getToken();
    }

		const response = await fetch(url, {
			method: method,
			headers: headers,
      body: body
		})

		if (response.status === 200) {
			return response.json();
		} if (response.status === 401 || response.status === 403) {
      if (secure) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          const retryHeaders = { ...headers, 'Authorization': 'Bearer ' + getToken() };
          const retryResponse = await fetch(url, { method: method, headers: retryHeaders, body: body });
          if (retryResponse.status === 200) return retryResponse.json();
          if (retryResponse.status === 422) {
            const error = await retryResponse.json();
            throw new Error(error.error);
          }
          if (retryResponse.status === 500) {
            const error = await retryResponse.json();
            throw new Error(error.exception);
          }
        }
      }
      redirectToLogin();
		} if (response.status === 422) {
			const error = await response.json();
			throw new Error( error.error);
		} if (response.status === 500) {
			const error = await response.json();
			throw new Error( error.exception);
		}
	} catch (error) {
		throw error;
	}
}

export function formatFileSize(bytes) {
  if (!bytes) return null;
  return bytes >= 1073741824
    ? (bytes / 1073741824).toFixed(1) + ' GB'
    : (bytes / 1048576).toFixed(1) + ' MB';
}

export async function downloadFile(url, filename, secure = true, onProgress = null) {
  try {
    const headers = {}

    if (secure) {
      headers['Authorization'] = 'Bearer ' + getToken();
    }

		let response = await fetch(url, { headers: headers })

    if ((response.status === 401 || response.status === 403) && secure) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        const retryHeaders = { ...headers, 'Authorization': 'Bearer ' + getToken() };
        response = await fetch(url, { headers: retryHeaders });
      } else {
        redirectToLogin();
        return;
      }
    }

    if (onProgress && response.body) {
      const contentLength = parseInt(response.headers.get('Content-Length') || '0', 10);
      const reader = response.body.getReader();
      const chunks = [];
      let loaded = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        loaded += value.length;
        onProgress({ loaded, total: contentLength });
      }

      const blob = new Blob(chunks);
      saveAs(blob, filename);
    } else {
      const blob = await response.blob();
      saveAs(blob, filename);
    }

	} catch (error) {
    throw new Error(error);
	}
}
